import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Badge,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Field,
  FieldLabel,
  Tooltip,
} from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import type {
  AdminFeatureFlagView,
  AdminFeatureFlagUserOverride,
  UserRole,
} from '@court-sync/shared';
import { USER_ROLES } from '@court-sync/shared';
import { Loader2, Info, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = ['All', 'UI', 'Functionality', 'Notifications', 'Beta', 'Mobile', 'Payment'] as const;

function truncateId(id: string, len = 8): string {
  if (id.length <= len + 3) return id;
  return `${id.slice(0, len)}…`;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

interface UserSearchHit {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export default function FeatureFlagsPage() {
  const [flags, setFlags] = useState<AdminFeatureFlagView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('All');
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailFlag, setDetailFlag] = useState<AdminFeatureFlagView | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [userHits, setUserHits] = useState<UserSearchHit[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchHit | null>(null);
  const [newUserOverrideEnabled, setNewUserOverrideEnabled] = useState(true);
  const [addingUserOverride, setAddingUserOverride] = useState(false);

  const [newRole, setNewRole] = useState<UserRole>('academy_owner');
  const [newRoleOverrideEnabled, setNewRoleOverrideEnabled] = useState(true);
  const [addingRoleOverride, setAddingRoleOverride] = useState(false);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = category !== 'All' ? { category } : undefined;
      const res = await adminBackofficeApi.getFeatureFlags(params);
      if (res.success && Array.isArray(res.data)) {
        setFlags(res.data);
      } else {
        setError(res.error || 'Failed to load feature flags');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Failed to load feature flags';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    if (!detailOpen || debouncedUserSearch.trim().length < 2) {
      setUserHits([]);
      return undefined;
    }
    let cancelled = false;
    setUserSearchLoading(true);
    adminBackofficeApi
      .getUsers({ search: debouncedUserSearch.trim(), page: 1, limit: 10 })
      .then((res: { success?: boolean; data?: { users?: UserSearchHit[] } }) => {
        if (cancelled) return;
        if (res.success && res.data?.users) {
          setUserHits(res.data.users);
        } else {
          setUserHits([]);
        }
      })
      .catch(() => {
        if (!cancelled) setUserHits([]);
      })
      .finally(() => {
        if (!cancelled) setUserSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedUserSearch, detailOpen]);

  const filteredFlags = useMemo(() => {
    if (category === 'All') return flags;
    return flags.filter((f) => f.category === category);
  }, [flags, category]);

  const openDetail = async (key: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailFlag(null);
    setUserSearch('');
    setSelectedUser(null);
    try {
      const res = await adminBackofficeApi.getFeatureFlag(key);
      if (res.success && res.data) {
        setDetailFlag(res.data);
      } else {
        toast.error(res.error || 'Failed to load flag details');
        setDetailOpen(false);
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to load flag details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (key: string) => {
    const res = await adminBackofficeApi.getFeatureFlag(key);
    if (res.success && res.data) {
      setDetailFlag(res.data);
      setFlags((prev) => prev.map((f) => (f.key === key ? res.data! : f)));
    }
  };

  const handleGlobalToggle = async (flag: AdminFeatureFlagView, enabled: boolean) => {
    if (flag.envControlled) return;
    setTogglingKey(flag.key);
    try {
      const res = await adminBackofficeApi.setFeatureFlag(flag.key, { enabled });
      if (res.success && res.data) {
        setFlags((prev) => prev.map((f) => (f.key === flag.key ? res.data! : f)));
        toast.success(`${flag.key} ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error(res.error || 'Failed to update flag');
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          (err as Error)?.message ||
          'Failed to update flag',
      );
    } finally {
      setTogglingKey(null);
    }
  };

  const handleAddUserOverride = async () => {
    if (!detailFlag || !selectedUser) return;
    setAddingUserOverride(true);
    try {
      const res = await adminBackofficeApi.setFeatureFlagUserOverride(detailFlag.key, selectedUser.id, {
        enabled: newUserOverrideEnabled,
      });
      if (res.success) {
        toast.success('User override added');
        setSelectedUser(null);
        setUserSearch('');
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to add user override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to add user override');
    } finally {
      setAddingUserOverride(false);
    }
  };

  const handleRemoveUserOverride = async (userId: string) => {
    if (!detailFlag) return;
    try {
      const res = await adminBackofficeApi.removeFeatureFlagUserOverride(detailFlag.key, userId);
      if (res.success) {
        toast.success('User override removed');
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to remove override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to remove override');
    }
  };

  const handleToggleUserOverride = async (override: AdminFeatureFlagUserOverride) => {
    if (!detailFlag || override.resolution !== 'resolved') return;
    try {
      const res = await adminBackofficeApi.setFeatureFlagUserOverride(detailFlag.key, override.userId, {
        enabled: !override.enabled,
      });
      if (res.success) {
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to update override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to update override');
    }
  };

  const handleAddRoleOverride = async () => {
    if (!detailFlag) return;
    setAddingRoleOverride(true);
    try {
      const res = await adminBackofficeApi.setFeatureFlagRoleOverride(detailFlag.key, newRole, {
        enabled: newRoleOverrideEnabled,
      });
      if (res.success) {
        toast.success('Role override added');
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to add role override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to add role override');
    } finally {
      setAddingRoleOverride(false);
    }
  };

  const handleRemoveRoleOverride = async (role: UserRole) => {
    if (!detailFlag) return;
    try {
      const res = await adminBackofficeApi.removeFeatureFlagRoleOverride(detailFlag.key, role);
      if (res.success) {
        toast.success('Role override removed');
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to remove override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to remove override');
    }
  };

  const handleToggleRoleOverride = async (role: UserRole, enabled: boolean) => {
    if (!detailFlag) return;
    try {
      const res = await adminBackofficeApi.setFeatureFlagRoleOverride(detailFlag.key, role, {
        enabled: !enabled,
      });
      if (res.success) {
        await refreshDetail(detailFlag.key);
      } else {
        toast.error(res.error || 'Failed to update override');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to update override');
    }
  };

  const renderUserOverrideRow = (override: AdminFeatureFlagUserOverride) => {
    const isResolved = override.resolution === 'resolved';
    const label =
      override.resolution === 'resolved'
        ? override.displayName || override.email || override.userId
        : override.resolution === 'orphaned'
          ? `Deleted user (${truncateId(override.userId)})`
          : `Legacy key (${truncateId(override.userId)})`;

    return (
      <div
        key={override.userId}
        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 p-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {override.resolution === 'orphaned' && (
              <Badge variant="destructive">Deleted user</Badge>
            )}
            {override.resolution === 'legacy_key' && (
              <Badge variant="outline">Legacy key</Badge>
            )}
            <span className="font-medium text-gray-900 dark:text-white">{label}</span>
            {isResolved && override.email && (
              <span className="text-sm text-muted-foreground">{override.email}</span>
            )}
          </div>
          {override.resolution === 'legacy_key' && (
            <p className="mt-1 text-xs text-muted-foreground">
              Leftover pre-migration key; remove to clean up.
            </p>
          )}
          {!isResolved && (
            <code className="mt-1 block text-xs text-muted-foreground font-mono">{override.userId}</code>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isResolved && (
            <Switch
              checked={override.enabled}
              onCheckedChange={() => handleToggleUserOverride(override)}
              aria-label={`Toggle override for ${label}`}
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleRemoveUserOverride(override.userId)}
            aria-label="Remove user override"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (error && flags.length === 0) {
    return (
      <div>
        <PageHeader
          title="Feature flags"
          help={
            <p>
              Platform runtime toggles for gradual rollout and beta features. These are separate from
              subscription tier gating (Programs, SMS, etc.).
            </p>
          }
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Feature flags"
        help={
          <p>
            Platform runtime toggles for gradual rollout and beta features. These are separate from
            subscription tier gating (Programs, SMS, etc.).
          </p>
        }
      />

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 p-3 text-sm text-blue-900 dark:text-blue-200">
        <Info className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
        <p>
          Changes may take up to 5 minutes to appear in web, landing, and mobile apps due to
          client-side caching. Hard refresh or re-login speeds this up.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <Button
            key={cat}
            type="button"
            variant={category === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategory(cat)}
          >
            {cat}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            {category === 'All' ? 'All flags' : `${category} flags`}
          </h2>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Key</th>
                    <th className="pb-2 pr-4 font-medium">Description</th>
                    <th className="pb-2 pr-4 font-medium">Default</th>
                    <th className="pb-2 pr-4 font-medium">Effective</th>
                    <th className="pb-2 pr-4 font-medium">Overrides</th>
                    <th className="pb-2 pr-4 font-medium">Updated</th>
                    <th className="pb-2 pr-4 font-medium">On</th>
                    <th className="pb-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {filteredFlags.map((flag) => {
                    const overrideCount =
                      flag.userOverrides.length + flag.roleOverrides.length;
                    const toggleDisabled = flag.envControlled || togglingKey === flag.key;
                    const toggle = (
                      <Switch
                        checked={flag.effectiveGlobal}
                        disabled={toggleDisabled}
                        onCheckedChange={(checked) => handleGlobalToggle(flag, checked)}
                        aria-label={`Toggle ${flag.key}`}
                      />
                    );

                    return (
                      <tr
                        key={flag.key}
                        className="border-b border-gray-100 dark:border-gray-800"
                      >
                        <td className="py-3 pr-4 font-mono text-xs">{flag.key}</td>
                        <td className="py-3 pr-4 max-w-xs">{flag.description}</td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline">{flag.staticDefault ? 'On' : 'Off'}</Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant={flag.effectiveGlobal ? 'default' : 'secondary'}>
                              {flag.effectiveGlobal ? 'On' : 'Off'}
                            </Badge>
                            {flag.envControlled && (
                              <Badge variant="outline">Env</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4">{overrideCount}</td>
                        <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
                          {formatDate(flag.updatedAt)}
                        </td>
                        <td className="py-3 pr-4">
                          {flag.envControlled ? (
                            <Tooltip
                              content={`Controlled by environment variable (${flag.envControlledBy})`}
                            >
                              {toggle}
                            </Tooltip>
                          ) : (
                            toggle
                          )}
                        </td>
                        <td className="py-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openDetail(flag.key)}
                          >
                            Manage
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredFlags.length === 0 && (
                <p className="py-8 text-center text-muted-foreground">No flags in this category.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailFlag ? detailFlag.key : 'Feature flag'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading || !detailFlag ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-md bg-muted/50 p-4 text-sm space-y-1">
                <p>{detailFlag.description}</p>
                <p>
                  <span className="text-muted-foreground">Static default:</span>{' '}
                  {detailFlag.staticDefault ? 'On' : 'Off'}
                </p>
                <p>
                  <span className="text-muted-foreground">DB global:</span>{' '}
                  {detailFlag.dbEnabled === null ? 'Not set' : detailFlag.dbEnabled ? 'On' : 'Off'}
                </p>
                <p>
                  <span className="text-muted-foreground">Effective global:</span>{' '}
                  {detailFlag.effectiveGlobal ? 'On' : 'Off'}
                  {detailFlag.envControlled && (
                    <span className="ml-2 text-amber-600 dark:text-amber-400">
                      (env: {detailFlag.envControlledBy})
                    </span>
                  )}
                </p>
              </div>

              <section>
                <h3 className="font-medium mb-3">User overrides</h3>
                <div className="space-y-2 mb-4">
                  {detailFlag.userOverrides.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No user overrides.</p>
                  ) : (
                    detailFlag.userOverrides.map(renderUserOverrideRow)
                  )}
                </div>

                {!detailFlag.envControlled && (
                  <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3">
                    <p className="text-sm font-medium">Add user override</p>
                    <Field>
                      <FieldLabel>Search user by email or name</FieldLabel>
                      <Input
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value);
                          setSelectedUser(null);
                        }}
                        placeholder="Type at least 2 characters…"
                      />
                    </Field>
                    {userSearchLoading && (
                      <p className="text-xs text-muted-foreground">Searching…</p>
                    )}
                    {userHits.length > 0 && !selectedUser && (
                      <ul className="border rounded-md divide-y dark:divide-gray-700 max-h-40 overflow-y-auto">
                        {userHits.map((u) => (
                          <li key={u.id}>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                              onClick={() => {
                                setSelectedUser(u);
                                setUserSearch(`${u.firstName} ${u.lastName} (${u.email})`);
                                setUserHits([]);
                              }}
                            >
                              {u.firstName} {u.lastName} — {u.email}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {selectedUser && (
                      <p className="text-xs text-muted-foreground">
                        Selected user id: <code className="font-mono">{selectedUser.id}</code>
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newUserOverrideEnabled}
                        onCheckedChange={setNewUserOverrideEnabled}
                        aria-label="New user override enabled"
                      />
                      <span className="text-sm">Enabled for user</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!selectedUser || addingUserOverride}
                      onClick={handleAddUserOverride}
                    >
                      {addingUserOverride ? 'Adding…' : 'Add override'}
                    </Button>
                  </div>
                )}
              </section>

              <section>
                <h3 className="font-medium mb-3">Role overrides</h3>
                <div className="space-y-2 mb-4">
                  {detailFlag.roleOverrides.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No role overrides.</p>
                  ) : (
                    detailFlag.roleOverrides.map((ro) => (
                      <div
                        key={ro.role}
                        className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 p-3"
                      >
                        <span className="font-medium capitalize">{ro.role.replace(/_/g, ' ')}</span>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={ro.enabled}
                            onCheckedChange={() => handleToggleRoleOverride(ro.role, ro.enabled)}
                            aria-label={`Toggle ${ro.role} override`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveRoleOverride(ro.role)}
                            aria-label="Remove role override"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {!detailFlag.envControlled && (
                  <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3">
                    <p className="text-sm font-medium">Add role override</p>
                    <Field>
                      <FieldLabel>Role</FieldLabel>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value as UserRole)}
                      >
                        {USER_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newRoleOverrideEnabled}
                        onCheckedChange={setNewRoleOverrideEnabled}
                        aria-label="New role override enabled"
                      />
                      <span className="text-sm">Enabled for role</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={addingRoleOverride}
                      onClick={handleAddRoleOverride}
                    >
                      {addingRoleOverride ? 'Adding…' : 'Add override'}
                    </Button>
                  </div>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
